import billingPolicy from '../config/billingPolicy.json';
import React from 'react';
import { AlertTriangle, CreditCard } from 'lucide-react';
import './SubscriptionBanner.css';

/**
 * Banner/Card component that displays subscription status warnings
 * Shows when subscription is past_due or canceled
 * @param {object} userData - User data from Firestore
 * @param {function} onManageBilling - Callback to open Stripe portal
 * @param {string} variant - 'banner' (top bar) or 'sidebar' (compact card)
 */
const SubscriptionBanner = ({ userData, onManageBilling, variant = 'banner' }) => {
  if (!billingPolicy.enabled || !userData) return null;
  
  const { subscriptionStatus, accessType, stripeCustomerId } = userData;
  
  // Only show for paid users with subscription issues
  if (accessType !== 'paid') return null;
  if (!subscriptionStatus || subscriptionStatus === 'active' || subscriptionStatus === 'trialing') return null;

  const getBannerContent = () => {
    switch (subscriptionStatus) {
      case 'past_due':
        return {
          type: 'warning',
          icon: <AlertTriangle size={variant === 'sidebar' ? 16 : 18} />,
          title: 'Payment Issue',
          message: variant === 'sidebar' 
            ? 'Update billing to avoid interruption.' 
            : 'There was a problem with your last payment. Please update your billing information to avoid service interruption.',
          actionText: 'Update Billing'
        };
      case 'canceled':
        return {
          type: 'error',
          icon: <AlertTriangle size={variant === 'sidebar' ? 16 : 18} />,
          title: 'Canceled',
          message: variant === 'sidebar'
            ? 'Reactivate to continue.'
            : 'Your subscription has been canceled. Reactivate to continue using all features.',
          actionText: 'Reactivate'
        };
      case 'unpaid':
        return {
          type: 'error',
          icon: <AlertTriangle size={variant === 'sidebar' ? 16 : 18} />,
          title: 'Unpaid',
          message: variant === 'sidebar'
            ? 'Payment failed. Update method.'
            : 'Your subscription payment failed. Please update your payment method.',
          actionText: 'Update'
        };
      case 'incomplete':
      case 'incomplete_expired':
        return {
          type: 'warning',
          icon: <CreditCard size={variant === 'sidebar' ? 16 : 18} />,
          title: 'Incomplete',
          message: variant === 'sidebar'
            ? 'Complete payment to activate.'
            : 'Your subscription setup is incomplete. Please complete payment to activate your account.',
          actionText: 'Complete'
        };
      default:
        return null;
    }
  };

  const content = getBannerContent();
  if (!content) return null;

  const handleAction = async () => {
    if (!stripeCustomerId || !onManageBilling) return;
    onManageBilling();
  };

  const baseClass = variant === 'sidebar' ? 'subscription-card' : 'subscription-banner';

  return (
    <div className={`${baseClass} ${baseClass}-${content.type}`}>
      <div className={`${baseClass}-content`}>
        <span className={`${baseClass}-icon`}>{content.icon}</span>
        <div className={`${baseClass}-text`}>
          <strong>{content.title}</strong>
          {variant === 'sidebar' && <br />}
          {variant !== 'sidebar' && ': '}
          {content.message}
        </div>
      </div>
      {stripeCustomerId && (
        <button className={`${baseClass}-action`} onClick={handleAction}>
          {content.actionText}
        </button>
      )}
    </div>
  );
};

export default SubscriptionBanner;
